import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cookie, Settings, Check, X } from 'lucide-react';
import safeStorage from '@/lib/safeStorage';

const CONSENT_KEY = 'smb_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = safeStorage.get(CONSENT_KEY);
    if (!consent) {
      // Small delay so it doesn't flash immediately
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (type) => {
    const data = {
      type, // 'all', 'necessary', 'custom'
      preferences: type === 'all' 
        ? { necessary: true, analytics: true, marketing: true }
        : type === 'necessary'
        ? { necessary: true, analytics: false, marketing: false }
        : preferences,
      timestamp: new Date().toISOString(),
    };
    safeStorage.set(CONSENT_KEY, data);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.15)] border border-[hsl(var(--border))] overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-[#238A92]/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Cookie size={20} className="text-[#238A92]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1F2A37] mb-1" style={{fontFamily:'Space Grotesk'}}>Informativa sui Cookie</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Questo sito utilizza cookie tecnici necessari al funzionamento e, previo tuo consenso, cookie analitici per migliorare la tua esperienza.
                Per maggiori informazioni consulta la nostra{' '}
                <Link to="/cookie-policy" className="text-[#238A92] hover:underline font-medium">Cookie Policy</Link>{' '}e la{' '}
                <Link to="/privacy-policy" className="text-[#238A92] hover:underline font-medium">Privacy Policy</Link>.
              </p>
            </div>
          </div>

          {showDetails && (
            <div className="mb-4 border rounded-lg p-4 space-y-3 bg-[hsl(var(--secondary))]/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Cookie necessari</p>
                  <p className="text-xs text-muted-foreground">Indispensabili per il funzionamento del sito. Non possono essere disattivati.</p>
                </div>
                <div className="w-10 h-6 bg-[#238A92] rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </div>
              <div className="border-t pt-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Cookie analitici</p>
                    <p className="text-xs text-muted-foreground">Ci aiutano a capire come utilizzi il sito per migliorarlo.</p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({...p, analytics: !p.analytics}))}
                    className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.analytics ? 'bg-[#238A92] justify-end' : 'bg-gray-300 justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </button>
                </div>
              </div>
              <div className="border-t pt-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Cookie di marketing</p>
                    <p className="text-xs text-muted-foreground">Utilizzati per mostrare contenuti personalizzati (social media, video).</p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({...p, marketing: !p.marketing}))}
                    className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.marketing ? 'bg-[#238A92] justify-end' : 'bg-gray-300 justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="text-muted-foreground"
            >
              <Settings size={14} className="mr-1" /> {showDetails ? 'Nascondi dettagli' : 'Personalizza'}
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => saveConsent(showDetails ? 'custom' : 'necessary')}
            >
              <X size={14} className="mr-1" /> {showDetails ? 'Salva preferenze' : 'Solo necessari'}
            </Button>
            <Button
              size="sm"
              className="bg-[#238A92] hover:bg-[#0F5E66] text-white"
              onClick={() => saveConsent('all')}
            >
              <Check size={14} className="mr-1" /> Accetta tutti
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
